/** Copyright (c) 2019 T.Rowe Price. All rights reserved. */

import { createSelector } from 'reselect';
import {
  SUBSCRIPTION_AK529,
  SUBSCRIPTION_MD_COLLEGE_INVESTMENT_PLAN,
  SUBSCRIPTION_TRP_COLLEGE_SAVINGS_PLAN,
  SUBSCRIPTION_TRP_PERSONAL_INVESTING
} from 'components/shared/GroupedAccountList/util';
import {
  MARKET_WRAP_UP_IDS,
  DAILY_PRICES_TYPE_ID,
  MONTHLY_FUND_TYPE_ID,
  SELECTED_FUNDS_SUBSCRIPTION_TYPE_ID,
  MY_FUNDS_SUBSCRIPTION_TYPE_ID,
  ALL_FUNDS_SUBSCRIPTION_TYPE_ID,
  EDUCATION_INSIGHTS_CATEGORY_ID,
  PRODUCTS_SERVICES_CATEGORY_ID,
  RESEARCH_SURVEYS_CATEGORY_ID
} from 'services/Preferences';
import { sortByKey } from 'services/Sorts';
import { isEqual } from 'lodash';
import { getNonViewOnlyProducts } from 'state/account/AccountSelectors';
import {
  getOnlyChangedElections,
  getOwnedOptOutElections
} from 'services/Subscriptions';

export const getSubscriptions = (state) => state.subscriptions || {};

export const getDocumentDelivery = (state) => state.documentDelivery || {};

export const getSubscriptionSaveStatus = (state) =>
  state.subscriptionsSave || {};

//Used for Global Preferences Center Migration need to filter the subscriptions response for owned products
export const getInitialSubscriptions = createSelector(
  getSubscriptionSaveStatus,
  getNonViewOnlyProducts,
  (subscriptionsSave, products) => {
    const { subscriptionsFromDB } = subscriptionsSave;
    if (subscriptionsFromDB) {
      const { optOutElections } = subscriptionsFromDB;
      return {
        ...subscriptionsFromDB,
        optOutElections: getOwnedOptOutElections(optOutElections, products)
      };
    }
  }
);

//Used for Global Preferences Center Migration need to filter the subscriptions response for owned products
export const getMappedSubscriptions = createSelector(
  getSubscriptions,
  getNonViewOnlyProducts,
  (subscriptions, products) => {
    if (subscriptions && subscriptions.optOutElections && products) {
      const { optOutElections } = subscriptions;

      return {
        ...subscriptions,
        optOutElections: getOwnedOptOutElections(optOutElections, products)
      };
    }
  }
);

export const getSubscriptionElections = createSelector(
  getMappedSubscriptions,
  (subscriptions) => subscriptions && subscriptions.optOutElections.length && subscriptions.optOutElections
);

const getDigitalSubscriptions = createSelector(
  getSubscriptions,
  (subscriptions) => subscriptions.digitalSubscriptions || {}
);

export const getComplianceSubscriptions = createSelector(
  getDocumentDelivery,
  (documentDelivery) => documentDelivery.complianceSubscriptions || []
);

export const getEducationInsightsOptOut = createSelector(
  getSubscriptionElections,
  (optOutElections) => {
    if (optOutElections) {
      return optOutElections.find((election) => {
        const { categoryId } = election;
        if (categoryId === EDUCATION_INSIGHTS_CATEGORY_ID) {
          return election;
        }
      });
    }
  }
);

export const getProductsServicesOptOut = createSelector(
  getSubscriptionElections,
  (optOutElections) => {
    if (optOutElections) {
      return optOutElections.find((election) => {
        const { categoryId } = election;
        if (categoryId === PRODUCTS_SERVICES_CATEGORY_ID) {
          return election;
        }
      });
    }
  }
);

export const getResearchSurveysOptOut = createSelector(
  getSubscriptionElections,
  (optOutElections) => {
    if (optOutElections) {
      return optOutElections.find((election) => {
        const { categoryId } = election;
        if (categoryId === RESEARCH_SURVEYS_CATEGORY_ID) {
          return election;
        }
      });
    }
  }
);

export const getIsSubscribedToMarketWrapUps = createSelector(
  getDigitalSubscriptions,
  (digitalSubscriptions) => {
    const { newsLetterIds = [] } = digitalSubscriptions;
    let hasMarketWrapup = false;
    MARKET_WRAP_UP_IDS.forEach((marketWrapUpId) => {
      if (
        newsLetterIds.some((newsletterId) => newsletterId === marketWrapUpId)
      ) {
        hasMarketWrapup = true;
      }
    });
    return hasMarketWrapup;
  }
);

export const getTaxForms = createSelector(
  getDigitalSubscriptions,
  (digitalSubscriptions) => {
    return digitalSubscriptions.taxForms;
  }
);

export const getIsSubscribedToTaxForms = createSelector(
  getTaxForms,
  (taxForms) => {
    return Boolean(taxForms);
  }
);

export const getNewsLetterSubscriptions = createSelector(
  getDigitalSubscriptions,
  (digitalSubscriptions) => digitalSubscriptions.newsLetterIds || []
);

export const getSelectDailyFundPrices = createSelector(
  getDigitalSubscriptions,
  (digitalSubscriptions) => {
    if (!digitalSubscriptions || !digitalSubscriptions.priceSubscriptions) {
      return false;
    }
    const {
      newsLetterIds,
      priceSubscriptions: { typeId, trpCodes }
    } = digitalSubscriptions;
    if (
      (trpCodes && trpCodes.length === 0 && typeId === -1) ||
      !newsLetterIds.some((id) => id === DAILY_PRICES_TYPE_ID)
    ) {
      return false;
    }
    return typeId === SELECTED_FUNDS_SUBSCRIPTION_TYPE_ID;
  }
);

export const getMonthlyPriceFunds = createSelector(
  getDigitalSubscriptions,
  (digitalSubscriptions) => {
    if (
      !digitalSubscriptions ||
      !digitalSubscriptions.performanceSubscriptions
    ) {
      return {
        broadAssetClassIds: [],
        trpCodes: []
      };
    }
    const {
      performanceSubscriptions: { broadAssetClassIds, trpCodes }
    } = digitalSubscriptions;
    return {
      broadAssetClassIds,
      trpCodes
    };
  }
);

export const getMyFundsDailyPrices = createSelector(
  getDigitalSubscriptions,
  (digitalSubscriptions) => {
    if (!digitalSubscriptions || !digitalSubscriptions.priceSubscriptions) {
      return false;
    }
    const {
      newsLetterIds,
      priceSubscriptions: { typeId, trpCodes }
    } = digitalSubscriptions;
    if (
      (trpCodes && trpCodes.length === 0 && typeId === -1) ||
      !newsLetterIds.some((id) => id === DAILY_PRICES_TYPE_ID)
    ) {
      return false;
    }
    return typeId === MY_FUNDS_SUBSCRIPTION_TYPE_ID;
  }
);

export const getAllFundDailyPrices = createSelector(
  getDigitalSubscriptions,
  (digitalSubscriptions) => {
    if (!digitalSubscriptions || !digitalSubscriptions.priceSubscriptions) {
      return false;
    }
    const {
      newsLetterIds,
      priceSubscriptions: { typeId, trpCodes }
    } = digitalSubscriptions;
    if (
      (trpCodes && trpCodes.length === 0 && typeId === -1) ||
      !newsLetterIds.some((id) => id === DAILY_PRICES_TYPE_ID)
    ) {
      return false;
    }
    return typeId === ALL_FUNDS_SUBSCRIPTION_TYPE_ID;
  }
);

export const getMyMonthlyFundPerformances = createSelector(
  getDigitalSubscriptions,
  (digitalSubscriptions) => {
    if (
      !digitalSubscriptions ||
      !digitalSubscriptions.performanceSubscriptions
    ) {
      return false;
    }
    const {
      newsLetterIds,
      performanceSubscriptions: { typeId, trpCodes }
    } = digitalSubscriptions;
    if (
      (trpCodes && trpCodes.length === 0 && typeId === -1) ||
      !newsLetterIds.some((id) => id === MONTHLY_FUND_TYPE_ID)
    ) {
      return false;
    }
    return typeId === MY_FUNDS_SUBSCRIPTION_TYPE_ID;
  }
);

export const getSelectMonthlyFundPrices = createSelector(
  getDigitalSubscriptions,
  (digitalSubscriptions) => {
    if (
      !digitalSubscriptions ||
      !digitalSubscriptions.performanceSubscriptions
    ) {
      return false;
    }
    const {
      newsLetterIds,
      performanceSubscriptions: { typeId, trpCodes }
    } = digitalSubscriptions;

    if (
      (trpCodes && trpCodes.length === 0 && typeId === -1) ||
      !newsLetterIds.some((id) => id === MONTHLY_FUND_TYPE_ID)
    ) {
      return false;
    }
    return typeId === SELECTED_FUNDS_SUBSCRIPTION_TYPE_ID;
  }
);

export const getAllMonthlyFundPrices = createSelector(
  getDigitalSubscriptions,
  (digitalSubscriptions) => {
    if (
      !digitalSubscriptions ||
      !digitalSubscriptions.performanceSubscriptions ||
      digitalSubscriptions.newsLetterIds.length === 0
    ) {
      return false;
    }
    const {
      newsLetterIds,
      performanceSubscriptions: { typeId, trpCodes }
    } = digitalSubscriptions;

    if (
      (trpCodes && trpCodes.length === 0 && typeId === -1) ||
      !newsLetterIds.some((id) => id === MONTHLY_FUND_TYPE_ID)
    ) {
      return false;
    }
    return typeId === ALL_FUNDS_SUBSCRIPTION_TYPE_ID;
  }
);

export const getDailyFundPricesSelected = createSelector(
  getDigitalSubscriptions,
  (digitalSubscriptions) => {
    if (
      !digitalSubscriptions ||
      !digitalSubscriptions.priceSubscriptions ||
      digitalSubscriptions.newsLetterIds.length === 0
    ) {
      return false;
    }
    const {
      newsLetterIds,
      priceSubscriptions: { typeId, trpCodes }
    } = digitalSubscriptions;

    if (
      (trpCodes && trpCodes.length === 0 && typeId === -1) ||
      !newsLetterIds.some((id) => id === DAILY_PRICES_TYPE_ID)
    ) {
      return false;
    }

    return (
      typeId === MY_FUNDS_SUBSCRIPTION_TYPE_ID ||
      typeId === SELECTED_FUNDS_SUBSCRIPTION_TYPE_ID ||
      typeId === ALL_FUNDS_SUBSCRIPTION_TYPE_ID
    );
  }
);

export const getPerformanceFundPricesSelected = createSelector(
  getDigitalSubscriptions,
  (digitalSubscriptions) => {
    if (
      !digitalSubscriptions ||
      !digitalSubscriptions.performanceSubscriptions ||
      digitalSubscriptions.newsLetterIds.length === 0
    ) {
      return false;
    }
    const {
      newsLetterIds,
      performanceSubscriptions: { typeId, trpCodes }
    } = digitalSubscriptions;

    if (
      (trpCodes && trpCodes.length === 0 && typeId === -1) ||
      !newsLetterIds.some((id) => id === MONTHLY_FUND_TYPE_ID)
    ) {
      return false;
    }
    return (
      typeId === MY_FUNDS_SUBSCRIPTION_TYPE_ID ||
      typeId === SELECTED_FUNDS_SUBSCRIPTION_TYPE_ID ||
      typeId === ALL_FUNDS_SUBSCRIPTION_TYPE_ID
    );
  }
);

export const getSelectedDailyFunds = createSelector(
  getDigitalSubscriptions,
  (digitalSubscriptions) => {
    if (!digitalSubscriptions || !digitalSubscriptions.priceSubscriptions) {
      return {
        broadAssetClassIds: [],
        trpCodes: []
      };
    }
    const {
      priceSubscriptions: { broadAssetClassIds, trpCodes }
    } = digitalSubscriptions;
    return {
      broadAssetClassIds,
      trpCodes
    };
  }
);

export const getDailyPrices = createSelector(
  getDigitalSubscriptions,
  (digitalSubscriptions) => {
    const dailyPrices = {
      hasDailyPrices: false,
      fundSelectionId: -1,
      broadAssetClassIds: [],
      trpCodes: []
    };
    const { priceSubscriptions, newsLetterIds } = digitalSubscriptions;
    if (newsLetterIds) {
      const isSubscribedToPricing = newsLetterIds.some(
        (id) => id === DAILY_PRICES_TYPE_ID
      );

      if (isSubscribedToPricing && priceSubscriptions) {
        const { typeId, broadAssetClassIds, trpCodes } = priceSubscriptions;
        dailyPrices.hasDailyPrices = true;
        dailyPrices.fundSelectionId = typeId;
        dailyPrices.broadAssetClassIds = broadAssetClassIds;
        dailyPrices.trpCodes = trpCodes;
      }
    }
    return dailyPrices;
  }
);

export const getMonthlyPerformance = createSelector(
  getDigitalSubscriptions,
  (digitalSubscriptions) => {
    const monthlyPerformance = {
      hasMonthlyPerformance: false,
      fundSelectionId: -1,
      broadAssetClassIds: [],
      trpCodes: []
    };
    const { performanceSubscriptions, newsLetterIds } = digitalSubscriptions;
    if (newsLetterIds) {
      const isSubscribedToMonthlyFundUpdates = newsLetterIds.some(
        (id) => id === MONTHLY_FUND_TYPE_ID
      );

      if (isSubscribedToMonthlyFundUpdates && performanceSubscriptions) {
        const { typeId, broadAssetClassIds, trpCodes } =
          performanceSubscriptions;
        monthlyPerformance.hasMonthlyPerformance = true;
        monthlyPerformance.fundSelectionId = typeId;
        monthlyPerformance.broadAssetClassIds = broadAssetClassIds;
        monthlyPerformance.trpCodes = trpCodes;
      }
    }
    return monthlyPerformance;
  }
);

export const getDocumentDeliveryEnrollments = createSelector(
  getComplianceSubscriptions,
  (complianceSubscriptions) =>
    complianceSubscriptions.reduce(
      (acc, { subscriptionId, subscribed }) => ({
        ...acc,
        [subscriptionId]: subscribed
      }),
      {}
    )
);
export const getFormattedOptoutElections = createSelector(
  getSubscriptions,
  (subscriptions) => {
    const optOutElections =
      subscriptions.optOutElections &&
      subscriptions.optOutElections.sort((a, b) =>
        sortByKey(a, b, 'categoryName')
      );
    const merged = optOutElections && optOutElections.reduce((r, { categoryName, ...rest }) => {
      let key;
      if (categoryName.includes('II - ')) {
        key = SUBSCRIPTION_TRP_PERSONAL_INVESTING;
      }
      if (categoryName.includes('MD529 - ')) {
        key = SUBSCRIPTION_MD_COLLEGE_INVESTMENT_PLAN;
      }
      if (categoryName.includes('AK529 - ')) {
        key = SUBSCRIPTION_AK529;
      }
      if (categoryName.includes('NA529 - ')) {
        key = SUBSCRIPTION_TRP_COLLEGE_SAVINGS_PLAN;
      }
      r[key] = r[key] || { key, optOutElections: [] };
      r[key]['optOutElections'].push({ categoryName, ...rest });
      return r;
    }, {});

    return Object.values(merged ? merged : []);
  }
);

export const getSubscriptionOptOutElections = createSelector(
  getSubscriptions,
  (subscriptions) => subscriptions?.optOutElections || []
);

export const getOptOutPreferencesSelectionsEqual = createSelector(
  getSubscriptions,
  getInitialSubscriptions,
  (subscriptions, initialSubscriptions) => {
    if (
      subscriptions &&
      subscriptions.optOutElections &&
      subscriptions.optOutElections.length &&
      initialSubscriptions &&
      initialSubscriptions.optOutElections &&
      initialSubscriptions.optOutElections.length
    ) {
      const { optOutElections: updatedOptOutElections } = subscriptions;
      const { optOutElections: initialOptOutElections } = initialSubscriptions;
      return (
        getOnlyChangedElections(initialOptOutElections, updatedOptOutElections)
          .length === 0
      );
    }

    return false;
  }
);

export const getDigitalSubscriptionsEqual = createSelector(
  getSubscriptions,
  getInitialSubscriptions,
  (subscriptions, initialSubscriptions) => {
    if (
      subscriptions &&
      subscriptions.digitalSubscriptions &&
      initialSubscriptions &&
      initialSubscriptions.digitalSubscriptions
    ) {
      const { digitalSubscriptions: updatedDigitalSubscriptions } =
        subscriptions;
      const { digitalSubscriptions: initialDigitalSubscriptions } =
        initialSubscriptions;
      return isEqual(updatedDigitalSubscriptions, initialDigitalSubscriptions);
    }

    return false;
  }
);

export const getShouldDisableSave = createSelector(
  getOptOutPreferencesSelectionsEqual,
  getDigitalSubscriptionsEqual,
  (optOutElectionsPreferencesEqual, digitalSubscriptionsEqual) => {
    return digitalSubscriptionsEqual && optOutElectionsPreferencesEqual;
  }
);
